import {
  aboveMaxSearchResultsLabel,
  maxSearchResults,
} from '@internal/blocks/SearchResultsBlock/maxSearchResults'
import { TPopularBrandsModelsForFromMachineCategoryPages } from '@internal/services/cmsClient'
import { tracker } from '@internal/utils/tracking/tracker'
import { Typography } from '@renderer-ui-library/mui'
import { BlocksContent, BlocksRenderer } from '@strapi/blocks-react-renderer'
import { MachineType } from '@internal/utils/machine/MachineType'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { memo, useCallback, useMemo } from 'react'
import { useMachineTypeName } from 'utils/machine/useMachineTypeName'
import styles from './searchResultsTitle.module.scss'

export interface SearchResultsTitleProps {
  brands: string[]
  model: string | undefined
  resultsCount: number
  machineType: MachineType | undefined
  popularBrands?: TPopularBrandsModelsForFromMachineCategoryPages[]
  popularModels?: TPopularBrandsModelsForFromMachineCategoryPages[]
  seoDescription?: BlocksContent | null
  currentModel: string | undefined
  hasLoadedOnce: boolean
}
export const SearchResultsTitle: React.FC<SearchResultsTitleProps> = memo(
  ({
    brands,
    model,
    resultsCount,
    machineType,
    popularBrands,
    popularModels,
    seoDescription,
    currentModel,
    hasLoadedOnce,
  }) => {
    const { t } = useTranslation(localeNamespace.common)
    const { getMachineTypeName } = useMachineTypeName()

    const title = useMemo(() => {
      let category: string | null = null
      if (brands.length === 1) {
        const brand = brands[0]

        if (model && brand) {
          category = `${brand} ${model}`
        } else if (brand && machineType) {
          category = `${brand} ${getMachineTypeName(
            machineType,
            resultsCount === 1 ? 'singular' : 'plural'
          )}`
        } else if (brand) {
          category = brand
        }
      }

      if (!category && machineType) {
        category = getMachineTypeName(
          machineType,
          resultsCount === 1 ? 'singular' : 'plural'
        )
      }

      const count =
        resultsCount >= maxSearchResults
          ? aboveMaxSearchResultsLabel
          : resultsCount

      return t(translations.searchResultsTitle, {
        category,
        amount: !hasLoadedOnce ? '' : count.toString(),
      })
    }, [
      brands,
      resultsCount,
      t,
      model,
      machineType,
      getMachineTypeName,
      hasLoadedOnce,
    ])

    const router = useRouter()

    const handlePopularLinkClick = useCallback(
      (ev: React.SyntheticEvent<HTMLAnchorElement>) => {
        ev.preventDefault()
        tracker.trackEvent({
          name: 'click',
          target_name: 'machine_category_page_popular_link.serp',
          is_interaction: true,
        }),
          router.push(ev.currentTarget.href)
      },
      [router]
    )

    const modelLinks = useMemo(
      () =>
        (brands.length !== 0 &&
          popularModels &&
          popularModels.filter(
            (model) => model.brand.toLowerCase() === brands[0].toLowerCase()
          )) ||
        [],
      [brands, popularModels]
    )

    const showPopularBrands =
      brands.length === 0 && popularBrands && popularBrands.length > 0
    const showPopularModels = modelLinks.length > 0

    return (
      <div className={styles.wrapper}>
        <div>
          <Typography variant='h2' component='h1'>
            {title}
          </Typography>

          {seoDescription && (
            <div className={styles.seoDescription}>
              <BlocksRenderer
                content={seoDescription}
                blocks={{
                  paragraph: ({ children }) => (
                    <Typography variant='body1' color='textSecondary'>
                      {children}
                    </Typography>
                  ),
                  link: ({ children, url }) => (
                    <Link href={url}>{children}</Link>
                  ),
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.popularLinksWrapper}>
          {showPopularBrands && (
            <>
              <Typography variant='caption' color='textSecondary'>
                <strong>
                  {t(translations.searchResultsPopularBrandsLabel)}:
                </strong>{' '}
              </Typography>
              {popularBrands.map((brand, index) => (
                <Link
                  href={brand.route}
                  onClick={handlePopularLinkClick}
                  key={brand.brand}
                >
                  <Typography variant='caption' color='text.primaryLight'>
                    {index < popularBrands.length - 1
                      ? `${brand.brand},`
                      : brand.brand}
                  </Typography>
                </Link>
              ))}
            </>
          )}
          {showPopularModels && (
            <>
              <Typography variant='caption' color='textSecondary'>
                <strong>
                  {t(translations.searchResultsPopularModelsLabel)}:
                </strong>{' '}
              </Typography>
              {modelLinks.map((model, index) => {
                const isActive =
                  model.model?.toLocaleLowerCase() ===
                  currentModel?.toLocaleLowerCase()

                return (
                  <Link
                    href={model.route}
                    onClick={handlePopularLinkClick}
                    key={model.model}
                  >
                    <Typography
                      variant='caption'
                      color={isActive ? 'secondary' : 'text.primaryLight'}
                    >
                      {index < modelLinks.length - 1
                        ? `${model.model},`
                        : model.model}
                    </Typography>
                  </Link>
                )
              })}
            </>
          )}
        </div>
      </div>
    )
  }
)

SearchResultsTitle.displayName = 'SearchResultsTitle'
